import { mapValues, orderBy, uniqBy } from 'lodash';
import { ChatState } from '../../store/state';
import { ChatAction, ChatActions } from '../actions/chat.action';
import { getLastMessageToken, getLastReceivedMessageToken, hasUnreadMessages } from '../../shared/utils/chat-utils';
import { GLYMPSE_PRO_CHAT_ORIGIN } from '../../shared/constants/chat.constants';

const mergeMessages = (existing, incoming) => orderBy(
  uniqBy([...(existing ?? []), ...incoming], 'id'),
  ['sequence_id', 'created_time']
);

const initialState: ChatState = {
  connecting: false,
  connected: false,
  reconnecting: false,
  reconnectAttempt: 0,
  error: null,
  chats: null,
  channelsSyncing: false,
  channelsSyncError: null
};

// "Read" state is tracked per-device (localStorage), so one console replying
// can't advance another console's own read marker. To keep the unread badge
// consistent across every console an agent might use, a customer message only
// counts as unread once it's newer than BOTH this device's own last-read
// marker AND the most recent reply from any agent - the latter is derived
// from the (already shared, synced-everywhere) message list itself, so a
// reply sent from any console clears the badge on all of them.
const countUnreadMessages = (chatChannel, messages) => {
  const lastReadMessage = Number(localStorage.getItem(getLastMessageToken(chatChannel))) || 0;
  const lastAgentReplySequenceId = messages?.reduce((max, message) => (
    message.origin === GLYMPSE_PRO_CHAT_ORIGIN && message.sequence_id > max ? message.sequence_id : max
  ), 0) ?? 0;
  const readThreshold = Math.max(lastReadMessage, lastAgentReplySequenceId);
  return messages?.filter(message => message.sequence_id > readThreshold && message.origin !== GLYMPSE_PRO_CHAT_ORIGIN)?.length;
};

const chatReducer = (state: ChatState = initialState, action: ChatAction) => {
  let chatId;
  let messages;
  switch (action.type) {
    case ChatActions.ConnectChat:
      return {
        ...state,
        connected: false,
        connecting: true
      };
    case ChatActions.ChatConnected:
      return {
        ...state,
        connected: true,
        connecting: false,
        reconnecting: false,
        reconnectAttempt: 0
      };
    case ChatActions.ChatConnectFailed:
      return {
        ...state,
        connecting: false,
        connected: false,
        error: action.payload
      };
    case ChatActions.ChatReconnectScheduled:
      return {
        ...state,
        connecting: false,
        connected: false,
        reconnecting: true,
        reconnectAttempt: action.payload.attempt
      };
    case ChatActions.ChatReconnectAttemptsExhausted:
      return {
        ...state,
        connecting: false,
        connected: false,
        reconnecting: false,
        error: 'Unable to reconnect to chat'
      };
    case ChatActions.DisconnectChat:
      return {
        ...state,
        connecting: false,
        connected: false,
        reconnecting: false,
        reconnectAttempt: 0,
        error: null,
      };
    case ChatActions.ChatStateChanged:
      return {
        ...state,
        connected: action.payload.current === 'connected',
        connecting: action.payload.current === 'connecting',
        reconnecting: ['unavailable', 'disconnected'].includes(action.payload.current)
          && action.payload.previous !== 'initialized',
      };
    case ChatActions.SubscribeChannel:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            chatInfo: action.payload.chatInfo,
            unreadMessagesCount: state?.chats?.[chatId]?.unreadMessagesCount
              ?? (hasUnreadMessages(chatId) ? 1 : 0)
          }
        }
      };
    case ChatActions.UnsubscribeChannel:
      // eslint-disable-next-line no-case-declarations
      const chats = {
        ...(state?.chats ?? {}),
      };
      // eslint-disable-next-line no-param-reassign
      delete chats?.[action.payload];
      return {
        ...state,
        chats
      };
    case ChatActions.SyncChatChannels:
      return {
        ...state,
        channelsSyncing: true,
        channelsSyncError: null
      };
    case ChatActions.SyncChatChannelsSuccess:
      return {
        ...state,
        channelsSyncing: false
      };
    case ChatActions.SyncChatChannelsFailed:
      return {
        ...state,
        channelsSyncing: false,
        channelsSyncError: action.payload
      };
    case ChatActions.ChannelSubscriptionFailed:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            subscriptionError: action.payload.error
          }
        }
      };
    case ChatActions.MessageReceived:
      chatId = action?.payload?.chat_channel;
      messages = mergeMessages(state?.chats?.[chatId]?.messages, [action?.payload?.message]);
      if (action?.payload?.message?.sequence_id && action?.payload?.message?.origin !== GLYMPSE_PRO_CHAT_ORIGIN) {
        localStorage.setItem(getLastReceivedMessageToken(chatId), `${action.payload.message.sequence_id}`);
      }
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            messages,
            unreadMessagesCount: countUnreadMessages(chatId, messages),
            lastSequenceId: messages?.[messages?.length - 1]?.sequence_id
          }
        }
      };
    case ChatActions.LoadMessages:
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [action.payload]: {
            ...(state?.chats?.[action.payload] ?? {}),
            loading: true,
            loaded: false,
            error: null,
            unreadMessagesCount: 0
          }
        }
      };
    case ChatActions.LoadMessagesSuccess:
      chatId = action?.payload?.chat_channel;
      messages = mergeMessages(state?.chats?.[chatId]?.messages, action.payload.messages ?? []);
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            chat_id: chatId,
            messages,
            loading: false,
            loaded: true,
            unreadMessagesCount: countUnreadMessages(chatId, messages),
            lastSequenceId: messages?.[messages?.length - 1]?.sequence_id
          }
        }
      };
    case ChatActions.LoadMessagesFailed:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            loading: false,
            loaded: false,
            error: action.payload.error
          }
        }
      };
    case ChatActions.SendMessage:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            sending: true,
            sent: false,
            error: null,
          }
        }
      };
    case ChatActions.SendMessageSuccess:
      chatId = action?.payload?.chat_channel;
      messages = mergeMessages(state?.chats?.[chatId]?.messages, [action.payload.message]);
      // Replying implies you've seen everything up to this point - advance the
      // read marker here rather than relying solely on ChatView's own "mark as
      // read on view" effect, which can lag behind a fast send (e.g. while the
      // chat's history is still loading).
      localStorage.setItem(getLastMessageToken(chatId), `${messages?.[messages?.length - 1]?.sequence_id}`);
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            messages,
            lastSequenceId: messages?.[messages?.length - 1]?.sequence_id ?? state?.chats?.[chatId]?.lastSequenceId,
            unreadMessagesCount: 0,
            sending: false,
            sent: true,
            error: null,
            message: ''
          }
        }
      };
    case ChatActions.SendMessageFailed:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            sending: false,
            sent: false,
            error: action.payload.error
          }
        }
      };
    case ChatActions.ChatNewMessageUpdate:
      chatId = action?.payload?.chat_channel;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            message: action.payload.message
          }
        }
      };
    case ChatActions.ReadMessages:
      chatId = action?.payload;
      return {
        ...state,
        chats: {
          ...(state?.chats ?? {}),
          [chatId]: {
            ...(state?.chats?.[chatId] ?? {}),
            unreadMessagesCount: 0
          }
        }
      };
    case ChatActions.ReadAllChats:
      return {
        ...state,
        chats: mapValues(state?.chats, (chat) => ({
          ...(chat ?? {}),
          unreadMessagesCount: 0
        }))
      };
    default:
      return state;
  }
};

export default chatReducer;
